import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { environment } from "../../../environments/environment";
import { ConsultationContextService } from "../services/consultation-context.service";
import { MsalAuthenticationService } from "@shared/services/msal-authentication.service";
import { AdvisorSessionExpiredService } from "@shared/services/advisor-session-expired.service";

@Injectable()
export class AdvisorConsultationContextInterceptor implements HttpInterceptor {
  constructor(
    private readonly consultationContextService: ConsultationContextService,
    private readonly msalAuthenticationService: MsalAuthenticationService,
    private readonly advisorSessionExpiredService: AdvisorSessionExpiredService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiUrl = request.url.startsWith(environment.apiConfig.uri);
    if (!isApiUrl || !this.shouldAddConsultationContext(request.url)) {
      return next.handle(request);
    }

    const context = this.consultationContextService.getContext();
    const isAdvisorMode = this.msalAuthenticationService.isAdvisorSsoEntry();

    // In advisor mode the context is mandatory for /me API calls.
    if (isAdvisorMode && !context) {
      this.msalAuthenticationService.clearFlags();
      this.advisorSessionExpiredService.notifyExpired();
      return throwError(() => new Error("Advisor consultation context missing or expired"));
    }

    if (!context) {
      return next.handle(request);
    }

    const requestWithContext = request.clone({
      url: this.injectMemberIdInPath(request.url, context.customerId),
      setParams: context.policyId && this.isMembersMeEndpoint(request.url) ? { policyId: context.policyId } : {},
    });

    return next.handle(requestWithContext);
  }

  private hasMeSegment(url: string): boolean {
    const [path] = url.split("?");
    return path.split("/").some(segment => segment === "me");
  }

  // policyId is only relevant for the members/me endpoint (event creation), not other /me endpoints.
  private isMembersMeEndpoint(url: string): boolean {
    const [path] = url.split("?");
    const segments = path.split("/").filter(Boolean);
    const meIndex = segments.lastIndexOf("me");
    return meIndex > 0 && segments[meIndex - 1] === "members";
  }

  private shouldAddConsultationContext(url: string): boolean {
    return this.hasMeSegment(url);
  }

  private injectMemberIdInPath(url: string, memberId: string): string {
    const [path, query] = url.split("?");

    if (!this.hasMeSegment(path)) {
      return url;
    }

    const normalizedPath = path.endsWith("/") ? path.slice(0, -1) : path;

    // avoid adding the memberId twice
    if (normalizedPath.endsWith(`/${memberId}`)) {
      return url;
    }

    const updatedPath = `${normalizedPath}/${memberId}`;
    return query ? `${updatedPath}?${query}` : updatedPath;
  }
}
